import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getActiveCompetitions,
  getUpcomingCompetitions,
  getRecentWinners,
  getTimeRemaining,
  competitionTypeLabels,
  competitionTypeIcons,
  votingLabels,
  votingIcons,
  tournamentLabels,
  type RfwCompetition,
} from '@/mocks/rfwCompetitions';

function formatCompact(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

/* ─── Mini VS Card ─── */
function MiniCompetitionCard({ comp }: { comp: RfwCompetition }) {
  const isActive = comp.status === 'active';
  const challengerWinning = comp.challenger.votes >= comp.opponent.votes;
  const hasWinner = comp.status === 'ended' && comp.winner;

  return (
    <div className="flex items-center gap-3 p-3 rounded-xl bg-background-50 hover:bg-background-100/70 transition-colors group cursor-pointer border border-background-200/50">
      {/* Challenger */}
      <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
        <div className="min-w-0 text-right">
          <p className={`text-[11px] font-bold truncate ${challengerWinning && isActive ? 'text-primary-600' : 'text-foreground-700'}`}>
            {comp.challenger.name}
          </p>
          <p className="text-[9px] text-foreground-400">{formatCompact(comp.challenger.score)} pts</p>
        </div>
        <div className="w-7 h-7 rounded-lg overflow-hidden bg-background-100 shrink-0 border border-background-200/50">
          {comp.challenger.logoUrl ? (
            <img src={comp.challenger.logoUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-background-200">
              <i className="ri-question-line text-[10px] text-foreground-300"></i>
            </div>
          )}
        </div>
      </div>

      {/* VS Badge */}
      <div className="shrink-0 flex flex-col items-center">
        <div className="w-7 h-7 rounded-full bg-accent-500 flex items-center justify-center">
          <span className="text-background-50 dark:text-foreground-950 font-black text-[9px]">VS</span>
        </div>
        {isActive && (
          <span className="text-[8px] font-bold text-emerald-600 mt-0.5 whitespace-nowrap">{getTimeRemaining(comp.endDate)}</span>
        )}
      </div>

      {/* Opponent */}
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <div className="w-7 h-7 rounded-lg overflow-hidden bg-background-100 shrink-0 border border-background-200/50">
          {comp.opponent.logoUrl ? (
            <img src={comp.opponent.logoUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-background-200">
              <i className="ri-question-line text-[10px] text-foreground-300"></i>
            </div>
          )}
        </div>
        <div className="min-w-0">
          <p className={`text-[11px] font-bold truncate ${!challengerWinning && isActive ? 'text-accent-600' : 'text-foreground-700'}`}>
            {comp.opponent.name}
          </p>
          <p className="text-[9px] text-foreground-400">{formatCompact(comp.opponent.score)} pts</p>
        </div>
      </div>

      {/* Status badge */}
      <div className="shrink-0">
        {isActive ? (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-emerald-50 text-[8px] font-bold text-emerald-600">
            <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse"></span>
            LIVE
          </span>
        ) : comp.status === 'upcoming' ? (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-50 text-[8px] font-bold text-amber-600">
            PRÓX.
          </span>
        ) : (
          <span className="text-sm">👑</span>
        )}
      </div>
    </div>
  );
}

/* ─── Winner Badge ─── */
function WinnerCompact({ comp }: { comp: RfwCompetition }) {
  if (!comp.winner) return null;
  return (
    <div className="flex items-center gap-2 p-2.5 rounded-xl bg-background-50 hover:bg-background-100/70 transition-colors cursor-pointer border border-amber-100">
      <div className="w-8 h-8 rounded-xl overflow-hidden bg-background-100 shrink-0 border border-amber-200">
        <img src={comp.winner.logoUrl} alt={comp.winner.name} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-bold text-foreground-700 truncate">{comp.winner.name}</p>
        <p className="text-[9px] text-foreground-400">{tournamentLabels[comp.tournament]}</p>
      </div>
      <span className="text-base">🥇</span>
    </div>
  );
}

export default function CompetenciasSection() {
  const navigate = useNavigate();
  const active = useMemo(() => getActiveCompetitions().slice(0, 6), []);
  const upcoming = useMemo(() => getUpcomingCompetitions().slice(0, 4), []);
  const winners = useMemo(() => getRecentWinners().slice(0, 4), []);

  return (
    <section className="py-16 md:py-24 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
              Competencias en Vivo
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground-950 tracking-tight">
              Competencias <span className="text-accent-600">RFW</span>
            </h2>
            <p className="mt-3 text-sm text-foreground-500 max-w-lg leading-relaxed">
              Batallas 1v1 entre radios, programas, locutores y clubes. Votá, competí y llevá a tu favorito a la cima. La rivalidad impulsa el crecimiento.
            </p>
          </div>
          <button
            onClick={() => navigate('/competencias')}
            className="text-accent-600 hover:text-accent-700 font-semibold text-sm flex items-center gap-1 whitespace-nowrap cursor-pointer"
          >
            Ver todas las competencias
            <i className="ri-arrow-right-line"></i>
          </button>
        </div>

        {/* Three columns grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Active Competitions */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
                <i className="ri-fire-line text-sm text-emerald-600"></i>
              </div>
              <h3 className="text-sm font-extrabold text-foreground-950 uppercase tracking-wider">Batallas Activas Ahora</h3>
              <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-[10px] font-bold text-emerald-600">
                {active.length} en vivo
              </span>
            </div>
            <div className="space-y-2">
              {active.map((comp) => (
                <div key={comp.id} onClick={() => navigate('/competencias')}>
                  <MiniCompetitionCard comp={comp} />
                </div>
              ))}
            </div>
          </div>

          {/* Right column: Upcoming + Winners */}
          <div className="space-y-6">
            {/* Upcoming */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center">
                  <i className="ri-calendar-line text-sm text-amber-600"></i>
                </div>
                <h3 className="text-xs font-extrabold text-foreground-950 uppercase tracking-wider">Próximas</h3>
              </div>
              <div className="space-y-2">
                {upcoming.map((comp) => (
                  <div key={comp.id} onClick={() => navigate('/competencias')} className="flex items-center gap-2 p-2.5 rounded-xl bg-background-100/70 hover:bg-background-200/70 transition-colors cursor-pointer">
                    <div className="w-8 h-8 rounded-lg overflow-hidden bg-background-200 shrink-0">
                      {comp.challenger.logoUrl ? (
                        <img src={comp.challenger.logoUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><i className="ri-question-line text-xs text-foreground-300"></i></div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] font-bold text-foreground-700 truncate">{comp.name}</p>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[9px] text-foreground-400">{tournamentLabels[comp.tournament]}</span>
                        <span className="text-[9px] text-foreground-300">·</span>
                        <span className="text-[9px] font-semibold text-amber-600">+{comp.prize.extraPoints} pts</span>
                      </div>
                    </div>
                    <i className="ri-arrow-right-s-line text-foreground-300 text-sm"></i>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Winners */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-yellow-100 flex items-center justify-center">
                  <i className="ri-trophy-line text-sm text-yellow-600"></i>
                </div>
                <h3 className="text-xs font-extrabold text-foreground-950 uppercase tracking-wider">Últimos Ganadores</h3>
              </div>
              <div className="space-y-2">
                {winners.map((comp) => (
                  <div key={comp.id} onClick={() => navigate('/competencias')}>
                    <WinnerCompact comp={comp} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-4 gap-3">
          {[
            { icon: 'ri-sword-line', title: 'Desafío', desc: 'Dos entidades compiten 1v1 en torneos semanales, mensuales o anuales.', color: 'bg-red-100 text-red-600' },
            { icon: 'ri-heart-line', title: 'Votación', desc: 'La comunidad vota con likes, reacciones, participación, encuestas o Score.', color: 'bg-rose-100 text-rose-600' },
            { icon: 'ri-trophy-line', title: 'Premiación', desc: 'El ganador recibe insignias, títulos, trofeo y puntos extra de RFW Score.', color: 'bg-amber-100 text-amber-600' },
            { icon: 'ri-vip-diamond-line', title: 'Hall of Fame', desc: 'Los campeones entran al Salón de la Fama y quedan en los récords históricos.', color: 'bg-purple-100 text-purple-600' },
          ].map((item) => (
            <div key={item.title} className="bg-background-100 rounded-2xl p-4 text-center">
              <div className={`w-9 h-9 rounded-xl ${item.color} flex items-center justify-center mx-auto mb-2`}>
                <i className={`${item.icon} text-sm`}></i>
              </div>
              <h4 className="text-xs font-extrabold text-foreground-950 mb-1">{item.title}</h4>
              <p className="text-[10px] text-foreground-500 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/competencias')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-bold rounded-xl transition-all cursor-pointer whitespace-nowrap"
          >
            <i className="ri-sword-line"></i>
            Ver Todas las Competencias
          </button>
        </div>
      </div>
    </section>
  );
}